/**
 * normalizeSignal — shared domain adapter (cycle-109.1 S227, FR-7 / D4).
 *
 * Pure, dependency-free leaf module. Maps either read DTO
 * (`WorldMonitorSignal` | `OsintSignal`) to a single `NormalizedSignal` shape
 * carrying a canonical cross-surface identity (`canonical_signal_id`).
 *
 * Architectural invariant (SDD §1.6): this module has **no runtime import** from
 * `api/`, `hooks/`, or any component — it imports only the two read-DTO *types*
 * via `import type` (erased at compile time → no runtime edge). Both consumers
 * (globe `GlobeCanvas.tsx`, ledger `InvestigationWorkspacePageV2.tsx` in cycle-105)
 * sit above `domain/` in the import graph, so no cycle is structurally possible.
 * Enforced by the acyclicity test in `__tests__/normalizeSignal.test.ts`.
 */
import type { OsintSignal } from '../../api/osint';
import type { WorldMonitorSignal } from '../../api/worldMonitor';

export interface NormalizedSignal {
  /** PRIMARY identity. `osint:{backendId}`. Stamped as data-signal-id. */
  canonical_signal_id: string;
  /** Underlying OsintSignal.id UUID (=== WorldMonitorSignal.id; same PK). */
  backendId: string;
  /** Defensive cross-feed reconciliation key. null on WM-live (not exposed). */
  contentHash: string | null;
  source: string;
  sourceFamily: string;
  /** 14-value taxonomy; drives DOMAIN_COLOR + isDomainVisible. */
  domain: string;
  headline: string;
  /** 'critical'|'high'|'moderate'|'low'|'routine'. Drives BEACON_SIZE_BY_SEV + SEV_COLOR. */
  severity: string;
  requiresAttention: boolean;
  /** null on OSINT-only signals (no lat/lon) -> NOT globe-plottable (FR-9). */
  geo: { lat: number; lon: number } | null;
  region: string;
  correlatedSignals: string[];
  receivedAt: string;
  /** true for WM-live (48h window); used for freshness skew (D4 risk 1). */
  isLive: boolean;
  investigationId: string | null;
}

/** `osint:` namespace prefix for the primary canonical id (SDD §3.2). */
export const CANONICAL_ID_PREFIX = 'osint';

/**
 * OSINT `significance` (routine|notable|significant|critical) → globe severity
 * tier (SDD §3.3). Unknown / null → 'routine' (SDD §5.1).
 */
const OSINT_SIGNIFICANCE_TO_SEVERITY: Record<string, string> = {
  critical: 'critical',
  significant: 'high',
  notable: 'moderate',
  routine: 'routine',
};

/**
 * Discriminant (SDD §5.1 / IMP-002): a nested `geo: {lat,lon}` with numeric
 * coordinates marks the WM-live branch. OSINT signals carry only free-text
 * `geo_region` (no `geo.lat`) → OSINT branch.
 */
function isWorldMonitorSignal(input: WorldMonitorSignal | OsintSignal): input is WorldMonitorSignal {
  const geo = (input as WorldMonitorSignal).geo;
  return !!geo && typeof geo.lat === 'number' && typeof geo.lon === 'number';
}

/**
 * Map either read DTO to a `NormalizedSignal`. Pure: no I/O, no React, no
 * `Date.now()`. The primary `canonical_signal_id` derives only from `backendId`
 * (`osint:{id}`) — both feeds share one PK namespace (SDD §3.2). The defensive
 * `hash:` fallback is the separate async `hashFallbackSignalId()` and is NEVER
 * the primary id.
 */
export function normalizeSignal(input: WorldMonitorSignal | OsintSignal): NormalizedSignal {
  const backendId = input.id;
  const canonical_signal_id = `${CANONICAL_ID_PREFIX}:${backendId}`;

  if (isWorldMonitorSignal(input)) {
    const severity = input.severity || 'routine';
    return {
      canonical_signal_id,
      backendId,
      contentHash: null, // not exposed on WM-live DTO (D4 nice-to-have, out of scope)
      source: input.source,
      sourceFamily: input.source_group ?? input.source,
      domain: input.domain || 'general',
      headline: input.title,
      severity,
      requiresAttention: severity === 'critical' || severity === 'high',
      geo: { lat: input.geo.lat, lon: input.geo.lon },
      region: input.region,
      correlatedSignals: input.correlated_signals ?? [],
      receivedAt: input.timestamp,
      isLive: true,
      investigationId: input.investigation_id,
    };
  }

  // OSINT branch — non-plottable on the globe this cycle (geo=null → FR-9).
  const severity = OSINT_SIGNIFICANCE_TO_SEVERITY[input.significance ?? ''] ?? 'routine';
  return {
    canonical_signal_id,
    backendId,
    contentHash: input.content_hash,
    source: input.source_id,
    sourceFamily: input.source_group,
    // No source_group→domain table is specified (SDD §3.3 "derive from
    // source_group"); OSINT is non-plottable this cycle and no test pins a
    // specific value, so carry source_group as the domain (defaults 'general').
    domain: input.source_group || 'general',
    headline: input.significance_reason ?? input.signal_type,
    severity,
    requiresAttention: severity === 'critical' || severity === 'high',
    geo: null,
    region: input.geo_region ?? '',
    correlatedSignals: [], // not present on the OSINT DTO
    receivedAt: input.collected_at,
    isLive: false,
    investigationId: input.investigation_id,
  };
}

// ── Defensive hash-fallback id (IMP-006, SDD §3.2) ─────────────────────────
// Used ONLY when a WM-live item is missing a backendId (never observed in
// practice). A field-hash match is a *soft* equality hint, NEVER authoritative
// identity, and MUST NOT gate access or settlement. Never the primary id.
// Uses Web Crypto SHA-256 over an RFC-8785-style canonical JSON — NOT the JS
// builtin `hash()` or an array index (those are process-salted; the exact bug
// at wm_upstream_adapter.py:354,500).

/** Minimal RFC-8785-style canonical JSON: object keys sorted recursively. */
function jcsCanonical(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(jcsCanonical).join(',')}]`;
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${jcsCanonical(obj[k])}`).join(',')}}`;
}

async function sha256Hex(input: string): Promise<string> {
  const bytes = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Defensive `hash:{sha256(JCS-canonical(fields))}` id (IMP-006). Async because
 * Web Crypto digest is async. Deterministic and key-order-independent. Returns
 * a soft cross-feed equality hint — never the primary canonical id.
 */
export async function hashFallbackSignalId(fields: Record<string, unknown> | ReadonlyArray<unknown>): Promise<string> {
  return `hash:${await sha256Hex(jcsCanonical(fields))}`;
}
